/**
 * Browser-side pull loop. Replaces apps/office-agent/internal/runtime.
 *
 * On each tick (default 30s):
 *   1. GET /v1/dashboard/files/pending
 *   2. For each unprocessed file: fetch ciphertext → "decrypt"
 *      (passthrough in L1; real age via typage in L2) → write to drop
 *      directory → POST /ack with plaintext BLAKE2b-256.
 *
 * The "decrypt" step is currently passthrough because the server-side
 * encrypt is also passthrough in L1 (apps/office-agent had a
 * PassthroughDecryptor for the same reason). When real age envelopes
 * land, swap pass-through for an age-WASM call using O_sk.
 *
 * In-memory state tracks which file_ids are in flight to dedupe across
 * ticks. Persistent state across reloads lives in IndexedDB; for L1 the
 * naive "fetch+process+ack" loop is fine because the server is the
 * source of truth for "is this file acked yet".
 */

import { blake2b } from "@noble/hashes/blake2b";

import { bytesToHex } from "@dovach/shared-crypto";

import { api, ApiError } from "./api";
import { ensureDropDirAccess, writeFileToDropDir } from "./drop-dir";
import { getOsk } from "./session-key";

const DEFAULT_INTERVAL_MS = 30_000;
const API_BASE: string =
  (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "https://api.dovach.local:8080";

interface PullLoopState {
  inFlight: Set<string>;
  lastError: string | null;
  lastTickAt: number | null;
  recentlyProcessed: string[]; // file_ids acked in the current session
}

const _state: PullLoopState = {
  inFlight: new Set(),
  lastError: null,
  lastTickAt: null,
  recentlyProcessed: [],
};

let _timer: number | null = null;

export function getPullLoopState(): Readonly<PullLoopState> {
  return _state;
}

export interface PullLoopOptions {
  /** Tick interval. Default 30s — matches the agent's poll interval. */
  intervalMs?: number;
  /** Returns true if polling should run on this tick. Honors the
   *  /settings/agent auto_pull + working_window switches. */
  enabled?: () => boolean;
}

/** Start the loop. Idempotent — safe to call from React StrictMode. */
export function startPullLoop(opts: PullLoopOptions = {}): void {
  if (_timer !== null) return;
  const interval = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  const tick = async () => {
    if (opts.enabled && !opts.enabled()) return;
    await runOneTick();
  };
  // Fire once immediately, then on interval.
  void tick();
  _timer = window.setInterval(tick, interval);
}

export function stopPullLoop(): void {
  if (_timer !== null) {
    window.clearInterval(_timer);
    _timer = null;
  }
}

/** Process a single tick. Exported so the inbox UI can wire a "Pull now" button. */
export async function runOneTick(): Promise<void> {
  _state.lastTickAt = Date.now();
  try {
    const osk = getOsk();
    if (!osk) {
      // Tab is locked — wait for the wizard's unlock screen.
      return;
    }
    const dirHandle = await ensureDropDirAccess();
    if (!dirHandle) {
      _state.lastError = "drop_dir_unavailable";
      return;
    }
    const resp = await api.dashboardFilesPending();
    for (const f of resp.files) {
      if (_state.inFlight.has(f.file_id)) continue;
      _state.inFlight.add(f.file_id);
      // Fire-and-forget per file — don't block other files behind a slow one.
      void processFile(f, dirHandle).finally(() => {
        _state.inFlight.delete(f.file_id);
      });
    }
    _state.lastError = null;
  } catch (err) {
    _state.lastError = err instanceof ApiError ? err.detail : "tick_failed";
  }
}

interface PendingFile {
  file_id: string;
  size: number;
  ciphertext_sha256: string;
  document_label: string | null;
  resolved_path: string;
  resolved_filename: string;
}

async function processFile(
  meta: PendingFile,
  dir: FileSystemDirectoryHandle,
): Promise<void> {
  // 1. Fetch ciphertext.
  const token = localStorage.getItem("dovach.access_token");
  const resp = await fetch(`${API_BASE}/v1/dashboard/files/${meta.file_id}/content`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "omit",
  });
  if (!resp.ok) {
    return;
  }
  const ciphertext = new Uint8Array(await resp.arrayBuffer());

  // 2. Integrity check — sha256(ciphertext) should match the metadata.
  const sha = await crypto.subtle.digest("SHA-256", ciphertext);
  const shaHex = bytesToHex(new Uint8Array(sha));
  if (shaHex !== meta.ciphertext_sha256) {
    _state.lastError = `integrity_mismatch_${meta.file_id}`;
    return;
  }

  // 3. "Decrypt" — passthrough in L1, matching server-side. Real age
  //    decrypt with O_sk lands in L2 (see plan §3.4 rewrite).
  const plaintext = ciphertext;

  // 4. Write to drop directory.
  try {
    await writeFileToDropDir(dir, meta.resolved_path, meta.resolved_filename, plaintext);
  } catch (err) {
    // Stash the failure so a "Pull now" / dev-console caller can see
    // it. Without this the cell sits at yellow→ forever and the user
    // has no clue why. Common cause: a reserved-char doc label that
    // the FSA refuses (sanitizeFsName handles most of these now, but
    // unicode quirks can still surface).
    const reason = err instanceof Error ? err.name : "unknown";
    _state.lastError = `drop_dir_write_failed:${meta.file_id}:${reason}`;
    return;
  }

  // 5. Compute plaintext BLAKE2b-256 + ack.
  const plaintextHash = bytesToHex(blake2b(plaintext, { dkLen: 32 }));
  try {
    await api.dashboardFileAck(meta.file_id, plaintextHash);
    _state.recentlyProcessed.push(meta.file_id);
    if (_state.recentlyProcessed.length > 100) {
      _state.recentlyProcessed.splice(0, _state.recentlyProcessed.length - 100);
    }
  } catch {
    // Server-side will surface this on the next tick — it's idempotent
    // because /ack is keyed on file_id.
  }
}
